import { useState, useEffect, Fragment } from "react";
import useInstanceInfo from "../hooks/useInstanceInfo";
import LegalLink from "./shared/LegalLink";

const roleDisplayNames = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  RISK_MANAGER: 'Risk Manager',
  CONTRIBUTOR: 'Contributor',
  APPROVER: 'Approver',
  VIEWER: 'Viewer',
};

// "A", "A and B", or "A, B, and C", as the sentence reads with every document configured.
const joinLinks = (links) => links.map((link, i) => (
  <Fragment key={i}>
    {i > 0 && (links.length === 2 ? ' and ' : i === links.length - 1 ? ', and ' : ', ')}
    {link}
  </Fragment>
));

// `loading` from the parent means it is submitting; `instanceLoading` is the installation's
// settings (which legal documents are configured) still loading. This step is never skipped:
// with no documents configured it still asks for the AI-output acknowledgement.
const UserInviteTermsAcceptance = ({ onAccept, onDecline, inviteDetails, loading: submitting }) => {
  const { info, loading: instanceLoading, error: instanceError, retry } = useInstanceInfo();
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [ready, setReady] = useState(false);

  const legal = info.legal;
  const docs = [
    { url: legal.termsOfUseUrl, label: 'Terms of Use' },
    { url: legal.dataProcessingAgreementUrl, label: 'Data Processing Agreement' },
    { url: legal.privacyPolicyUrl, label: 'Privacy Policy' },
  ].filter((doc) => doc.url);
  const loaded = !instanceLoading && !instanceError;
  const hasDocs = docs.length > 0;

  // 800ms after the checkboxes render, fade hint + enable checkboxes + fill progress bar
  useEffect(() => {
    if (!loaded) return undefined;
    const timer = setTimeout(() => setReady(true), 800);
    return () => clearTimeout(timer);
  }, [loaded]);

  const canAccept = (check1 || !hasDocs) && check2 && !submitting;
  const orgName = inviteDetails?.organizationName;
  const roleName = inviteDetails?.role ? (roleDisplayNames[inviteDetails.role] || inviteDetails.role) : null;

  const header = (
    <>
      {/* Header */}
      <div className="p-5 pb-4 border-b border-[#e5e5e5]">
        <div>
          <span className="logo-wordmark-text logo-wordmark-text-gradient block mb-2.5">AXARIS™</span>
          <div>
            <h1 className="text-[15px] font-medium text-[#1d1d1f]">Welcome</h1>
            <p className="text-[13px] text-[#6e6e73]">
              {orgName
                ? `You have been invited to join ${orgName}`
                : "You have been invited to join a workspace"}
            </p>
            {roleName && (
              <span className="inline-block mt-1.5 text-[11px] font-medium px-2 py-0.5 rounded bg-[#E6F1FB] text-[#0C447C]">
                {roleName}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const declineButton = (
    <button
      onClick={onDecline}
      disabled={submitting}
      className="px-5 py-2.5 text-[13px] text-[#6e6e73] border border-[#d2d2d7] rounded-lg hover:bg-[#f5f5f7] transition-colors disabled:opacity-50"
    >
      Decline
    </button>
  );

  const card = (body) => (
    <div className="flex items-center justify-center min-h-screen bg-[#fafafa] p-4">
      <div className="w-full max-w-[480px] bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden">
        {header}
        <div className="p-5">{body}</div>
      </div>
    </div>
  );

  if (instanceLoading) {
    return card(
      <>
        <p className="text-[13px] text-[#6e6e73] text-center mb-5">Loading…</p>
        <div className="flex justify-end gap-2">
          {declineButton}
          <button
            disabled
            className="px-5 py-2.5 text-[13px] font-medium text-white bg-[#378ADD] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            Accept
          </button>
        </div>
      </>
    );
  }

  if (instanceError) {
    return card(
      <>
        <p className="text-[13px] text-[#6e6e73] text-center mb-5">
          {"We couldn't load the terms for this installation."}
        </p>
        <div className="flex justify-end gap-2">
          {declineButton}
          <button
            onClick={retry}
            className="px-5 py-2.5 text-[13px] font-medium text-white bg-[#378ADD] rounded-lg hover:bg-[#185FA5] transition-colors flex items-center justify-center gap-2"
          >
            Try again
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#fafafa] p-4">
      <div className="w-full max-w-[480px] bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden">
        {header}

        {/* Progress bar */}
        <div className="h-[2px] bg-[#e5e5e5] overflow-hidden">
          <div
            className="h-full bg-[#378ADD] transition-all duration-200"
            style={{ width: ready ? '100%' : '0%' }}
          />
        </div>

        {/* Body */}
        <div className="p-5">
          {/* Hint text */}
          {hasDocs && (
            <p
              className="text-[12px] text-[#86868b] text-center mb-3 transition-opacity duration-300"
              style={{ opacity: ready ? 0 : 1 }}
            >
              Review the documents linked below before accepting
            </p>
          )}

          {/* Checkboxes */}
          <div className="flex flex-col gap-2.5 mb-5">
            {hasDocs && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={check1}
                  onChange={(e) => setCheck1(e.target.checked)}
                  disabled={!ready}
                  className="w-[15px] h-[15px] mt-0.5 flex-shrink-0 accent-[#378ADD]"
                />
                <span className="text-[13px] text-[#6e6e73] leading-relaxed">
                  I have read and accept the{' '}
                  {joinLinks(docs.map((doc) => (
                    <LegalLink key={doc.label} url={doc.url} newTab className="text-[#378ADD] no-underline hover:underline">{doc.label}</LegalLink>
                  )))}
                </span>
              </label>
            )}

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={check2}
                onChange={(e) => setCheck2(e.target.checked)}
                disabled={!ready}
                className="w-[15px] h-[15px] mt-0.5 flex-shrink-0 accent-[#378ADD]"
              />
              <span className="text-[13px] text-[#6e6e73] leading-relaxed">
                I understand that all AI outputs must be independently validated before implementation
                and I accept responsibility for any actions I take based on platform outputs
              </span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2">
            {declineButton}
            <button
              onClick={onAccept}
              disabled={!canAccept}
              className="px-5 py-2.5 text-[13px] font-medium text-white bg-[#378ADD] rounded-lg hover:bg-[#185FA5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && (
                <div className="animate-spin rounded-full border-2 border-transparent border-t-white border-r-white h-3.5 w-3.5" />
              )}
              {submitting ? 'Setting up...' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInviteTermsAcceptance;
